/**
 * GltfLoader.ts — minimal glTF 2.0 binary (.glb) parser.
 *
 * Scope intentionally tiny:
 *   - .glb only (single-file binary container)
 *   - extracts every primitive's POSITION, NORMAL, COLOR_0 (optional), and
 *     indices into a flat MeshAsset (interleaved 9 f32 per vertex).
 *   - applies node transforms (no skinning, no morph targets, no animations).
 *   - ignores textures, cameras, lights; uses material baseColorFactor only.
 *
 * If a primitive has no COLOR_0, every vertex gets the colour from its
 * material's pbrMetallicRoughness.baseColorFactor (or pure white as fallback).
 *
 * The result is a single MeshAsset per glb file (all primitives merged).
 * That's fine for our small CC0 props (boats, dock, lighthouse, trees).
 */

import type { MeshAsset } from "./MeshAsset";
import { MESH_VERTEX_STRIDE } from "./MeshAsset";

export interface GltfMeshAsset extends MeshAsset {
  /** Local-space AABB for collisions and culling. */
  bbox: { min: [number, number, number]; max: [number, number, number] };
  vertexCount: number;
  indexCount: number;
  /**
   * Local-space collision boxes extracted from child nodes that follow
   * the Unreal/FBX collision-helper naming convention (`UBX_*`,
   * `UCX_*`, `USP_*`, `UCP_*`). Both Blender addons we target produce
   * such nodes: `andyp123/blender_vhacd` (legacy V-HACD) and
   * `olitheolix/blender-convex-decomposition` (V-HACD or CoACD,
   * exports under the `UCX_*` prefix). When present, these are the
   * authored colliders and the runtime should use them instead of
   * deriving an AABB from the visual {@link bbox}. The helper nodes
   * are excluded from the rendered geometry.
   *
   * Today every prefix collapses to one AABB per node — exact for
   * boxes, conservative for convex hulls and capsules. Real
   * polygon-vs-convex SAT is a follow-up.
   */
  collisionBoxes?: Array<{
    min: [number, number, number];
    max: [number, number, number];
  }>;
}

const GLB_MAGIC = 0x46546c67; // 'glTF'
const CHUNK_JSON = 0x4e4f534a; // 'JSON'
const CHUNK_BIN = 0x004e4942; // 'BIN\0'

const COMPONENT_BYTES: Record<number, number> = {
  5120: 1,
  5121: 1, // byte / unsigned byte
  5122: 2,
  5123: 2, // short / unsigned short
  5125: 4, // unsigned int
  5126: 4, // float
};

const COMPONENT_CTOR: Record<
  number,
  new (b: ArrayBuffer, o: number, n: number) => ArrayBufferView
> = {
  5120: Int8Array as never,
  5121: Uint8Array as never,
  5122: Int16Array as never,
  5123: Uint16Array as never,
  5125: Uint32Array as never,
  5126: Float32Array as never,
};

const TYPE_ELEMENTS: Record<string, number> = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
  MAT2: 4,
  MAT3: 9,
  MAT4: 16,
};

interface GltfJson {
  buffers: { byteLength: number; uri?: string }[];
  bufferViews: {
    buffer: number;
    byteOffset?: number;
    byteLength: number;
    byteStride?: number;
  }[];
  accessors: {
    bufferView: number;
    byteOffset?: number;
    componentType: number;
    count: number;
    type: keyof typeof TYPE_ELEMENTS;
  }[];
  meshes: {
    primitives: {
      attributes: Record<string, number>;
      indices?: number;
      material?: number;
    }[];
  }[];
  nodes: {
    name?: string;
    mesh?: number;
    children?: number[];
    matrix?: number[]; // column-major 4x4
    translation?: [number, number, number];
    rotation?: [number, number, number, number]; // xyzw quaternion
    scale?: [number, number, number];
  }[];
  scenes: { nodes: number[] }[];
  scene?: number;
  images?: { uri?: string; bufferView?: number; mimeType?: string }[];
  textures?: { source?: number }[];
  materials?: {
    name?: string;
    pbrMetallicRoughness?: {
      baseColorFactor?: [number, number, number, number];
      baseColorTexture?: { index: number };
      metallicFactor?: number;
      roughnessFactor?: number;
    };
  }[];
}

/**
 * Parse a .glb ArrayBuffer into a single merged MeshAsset.
 *
 * All primitives across all meshes referenced by the default scene are
 * concatenated (with their node transforms applied to vertex positions and
 * normals).
 *
 * `materialColors`: optional map keyed by glTF material name. When a
 * primitive references a material whose name matches an entry, its
 * vertices are coloured with the mapped RGB (replacing whatever
 * baseColorFactor / COLOR_0 would otherwise contribute).
 */
export interface ParseGlbOptions {
  /** Map glTF material names → vertex colour. */
  materialColors?: Record<string, [number, number, number]>;
  /**
   * Predicate run on every node name during scene traversal. Returning
   * true SKIPS that node's mesh (and only its mesh — children are still
   * visited). Useful to keep anchor-only Empties (e.g. `Skipper.Head`,
   * `Cleat.Bow`) in the glb for position lookup but invisible at render.
   */
  hideNode?: (name: string) => boolean;
}

export function parseGlb(
  arrayBuffer: ArrayBuffer,
  optsOrMaterialColors?:
    | Record<string, [number, number, number]>
    | ParseGlbOptions,
): GltfMeshAsset {
  // Back-compat: legacy callers pass materialColors directly.
  const opts: ParseGlbOptions =
    optsOrMaterialColors && "materialColors" in optsOrMaterialColors
      ? (optsOrMaterialColors as ParseGlbOptions)
      : {
          materialColors: optsOrMaterialColors as
            | Record<string, [number, number, number]>
            | undefined,
        };
  const materialColors = opts.materialColors;
  const hideNode = opts.hideNode;
  const dv = new DataView(arrayBuffer);
  if (dv.getUint32(0, true) !== GLB_MAGIC) {
    throw new Error("parseGlb: not a .glb file (magic mismatch)");
  }
  if (dv.getUint32(4, true) !== 2) {
    throw new Error("parseGlb: only glTF 2.0 supported");
  }
  // const totalLength = dv.getUint32(8, true);

  // Chunk 0: JSON
  const jsonLen = dv.getUint32(12, true);
  const jsonType = dv.getUint32(16, true);
  if (jsonType !== CHUNK_JSON)
    throw new Error("parseGlb: first chunk must be JSON");
  const jsonBytes = new Uint8Array(arrayBuffer, 20, jsonLen);
  const json = JSON.parse(new TextDecoder().decode(jsonBytes)) as GltfJson;

  // Chunk 1: BIN (optional in the spec, but every export tool emits it)
  let bin: ArrayBuffer | null = null;
  const chunk1Off = 20 + jsonLen;
  if (chunk1Off + 8 <= arrayBuffer.byteLength) {
    const binLen = dv.getUint32(chunk1Off, true);
    const binType = dv.getUint32(chunk1Off + 4, true);
    if (binType === CHUNK_BIN) {
      bin = arrayBuffer.slice(chunk1Off + 8, chunk1Off + 8 + binLen);
    }
  }
  if (!bin) throw new Error("parseGlb: missing BIN chunk");

  const accessor = (idx: number): ArrayBufferView => {
    const acc = json.accessors[idx];
    if (!acc) throw new Error(`parseGlb: missing accessor ${idx}`);
    const view = json.bufferViews[acc.bufferView];
    if (!view)
      throw new Error(`parseGlb: missing bufferView ${acc.bufferView}`);
    const baseOffset = (view.byteOffset ?? 0) + (acc.byteOffset ?? 0);
    const elements = TYPE_ELEMENTS[acc.type];
    const compBytes = COMPONENT_BYTES[acc.componentType];
    if (!elements || !compBytes) {
      throw new Error(
        `parseGlb: unsupported accessor type ${acc.type}/${acc.componentType}`,
      );
    }
    if (view.byteStride && view.byteStride !== elements * compBytes) {
      // Interleaved: copy out into a tightly packed array.
      const Ctor = COMPONENT_CTOR[acc.componentType]!;
      const out = new (Ctor as unknown as new (
        n: number,
      ) => ArrayBufferView & { length: number; [i: number]: number })(
        acc.count * elements,
      );
      const src = new DataView(bin!);
      for (let i = 0; i < acc.count; i++) {
        for (let e = 0; e < elements; e++) {
          const off = baseOffset + i * view.byteStride + e * compBytes;
          let v: number;
          switch (acc.componentType) {
            case 5120:
              v = src.getInt8(off);
              break;
            case 5121:
              v = src.getUint8(off);
              break;
            case 5122:
              v = src.getInt16(off, true);
              break;
            case 5123:
              v = src.getUint16(off, true);
              break;
            case 5125:
              v = src.getUint32(off, true);
              break;
            case 5126:
              v = src.getFloat32(off, true);
              break;
            default:
              v = 0;
          }
          (out as unknown as { [i: number]: number })[i * elements + e] = v;
        }
      }
      return out as ArrayBufferView;
    }
    const Ctor = COMPONENT_CTOR[acc.componentType]!;
    return new Ctor(bin!, baseOffset, acc.count * elements);
  };

  // Build node-world matrices iteratively from the default scene.
  const sceneIdx = json.scene ?? 0;
  const sceneNodes = json.scenes[sceneIdx]?.nodes ?? [];

  // Output buffers (we'll grow them via JS arrays then convert at the end).
  const outPos: number[] = [];
  const outNrm: number[] = [];
  const outCol: number[] = [];
  const outIdx: number[] = [];
  let bbMinX = +Infinity,
    bbMinY = +Infinity,
    bbMinZ = +Infinity;
  let bbMaxX = -Infinity,
    bbMaxY = -Infinity,
    bbMaxZ = -Infinity;
  // Authored collision boxes (Unreal-style `UBX_*` node convention).
  // Each entry is the local-space AABB of one such node's vertices.
  const collisionBoxes: Array<{
    min: [number, number, number];
    max: [number, number, number];
  }> = [];

  const nodeMatrix = (idx: number): Float32Array => {
    const n = json.nodes[idx];
    const m = new Float32Array(16);
    if (n?.matrix) {
      for (let i = 0; i < 16; i++) m[i] = n.matrix[i] ?? 0;
      return m;
    }
    // TRS → matrix (column-major, same as glTF spec).
    const t = n?.translation ?? [0, 0, 0];
    const r = n?.rotation ?? [0, 0, 0, 1];
    const s = n?.scale ?? [1, 1, 1];
    const [qx, qy, qz, qw] = r;
    const xx = qx * qx,
      yy = qy * qy,
      zz = qz * qz;
    const xy = qx * qy,
      xz = qx * qz,
      yz = qy * qz;
    const wx = qw * qx,
      wy = qw * qy,
      wz = qw * qz;
    m[0] = (1 - 2 * (yy + zz)) * s[0]!;
    m[1] = 2 * (xy + wz) * s[0]!;
    m[2] = 2 * (xz - wy) * s[0]!;
    m[3] = 0;
    m[4] = 2 * (xy - wz) * s[1]!;
    m[5] = (1 - 2 * (xx + zz)) * s[1]!;
    m[6] = 2 * (yz + wx) * s[1]!;
    m[7] = 0;
    m[8] = 2 * (xz + wy) * s[2]!;
    m[9] = 2 * (yz - wx) * s[2]!;
    m[10] = (1 - 2 * (xx + yy)) * s[2]!;
    m[11] = 0;
    m[12] = t[0]!;
    m[13] = t[1]!;
    m[14] = t[2]!;
    m[15] = 1;
    return m;
  };

  const mulMat = (a: Float32Array, b: Float32Array): Float32Array => {
    const o = new Float32Array(16);
    for (let c = 0; c < 4; c++) {
      for (let r = 0; r < 4; r++) {
        let s = 0;
        for (let k = 0; k < 4; k++) s += a[k * 4 + r]! * b[c * 4 + k]!;
        o[c * 4 + r] = s;
      }
    }
    return o;
  };

  const transformPoint = (
    m: Float32Array,
    x: number,
    y: number,
    z: number,
  ): [number, number, number] => [
    m[0]! * x + m[4]! * y + m[8]! * z + m[12]!,
    m[1]! * x + m[5]! * y + m[9]! * z + m[13]!,
    m[2]! * x + m[6]! * y + m[10]! * z + m[14]!,
  ];
  const transformDir = (
    m: Float32Array,
    x: number,
    y: number,
    z: number,
  ): [number, number, number] => {
    // Assume uniform scale (no per-axis scaling for normals).
    const ox = m[0]! * x + m[4]! * y + m[8]! * z;
    const oy = m[1]! * x + m[5]! * y + m[9]! * z;
    const oz = m[2]! * x + m[6]! * y + m[10]! * z;
    const len = Math.hypot(ox, oy, oz) || 1;
    return [ox / len, oy / len, oz / len];
  };

  const visit = (nodeIdx: number, parent: Float32Array): void => {
    const node = json.nodes[nodeIdx];
    if (!node) return;
    const local = nodeMatrix(nodeIdx);
    const world = mulMat(parent, local);
    const isCollider = !!(
      node.name &&
      // Unreal/FBX collision-helper conventions, also used by the
      // Blender addons `andyp123/blender_vhacd` (UBX_/UCX_/USP_) and
      // `olitheolix/blender-convex-decomposition` (UCX_ via V-HACD or
      // CoACD). For now every recognised prefix collapses to an
      // axis-aligned bbox of the helper node's vertices — that is
      // tight for `UBX_*` (already a box) and conservative for
      // `UCX_*` (the AABB of one convex chunk, not the original
      // mesh). Real polygon-vs-convex SAT lands later.
      (node.name.startsWith("UBX_") ||
        node.name.startsWith("UCX_") ||
        node.name.startsWith("USP_") ||
        node.name.startsWith("UCP_"))
    );
    if (isCollider) {
      // Compute a local-space AABB from this node's mesh vertices and
      // skip emitting any visible geometry. We accumulate vertex world
      // positions into a fresh AABB — the "world" here is the GLB's
      // local space, which is exactly what the runtime placer needs.
      if (node.mesh !== undefined) {
        const mesh = json.meshes[node.mesh];
        if (mesh) {
          let cMinX = +Infinity,
            cMinY = +Infinity,
            cMinZ = +Infinity;
          let cMaxX = -Infinity,
            cMaxY = -Infinity,
            cMaxZ = -Infinity;
          for (const prim of mesh.primitives) {
            const posAcc = prim.attributes.POSITION;
            if (posAcc === undefined) continue;
            const positions = accessor(posAcc) as Float32Array;
            const vertexCount = positions.length / 3;
            for (let i = 0; i < vertexCount; i++) {
              const [wx, wy, wz] = transformPoint(
                world,
                positions[i * 3]!,
                positions[i * 3 + 1]!,
                positions[i * 3 + 2]!,
              );
              if (wx < cMinX) cMinX = wx;
              if (wx > cMaxX) cMaxX = wx;
              if (wy < cMinY) cMinY = wy;
              if (wy > cMaxY) cMaxY = wy;
              if (wz < cMinZ) cMinZ = wz;
              if (wz > cMaxZ) cMaxZ = wz;
            }
          }
          if (cMinX < cMaxX) {
            collisionBoxes.push({
              min: [cMinX, cMinY, cMinZ],
              max: [cMaxX, cMaxY, cMaxZ],
            });
          }
        }
      }
      // Walk children for nested colliders, but don't recurse into
      // rendering them either.
      if (node.children) for (const c of node.children) visit(c, world);
      return;
    }
    const skipMesh = !!(hideNode && node.name && hideNode(node.name));
    if (!skipMesh && node.mesh !== undefined) {
      const mesh = json.meshes[node.mesh];
      if (mesh) {
        for (const prim of mesh.primitives) {
          const baseVertex = outPos.length / 3;
          const posAcc = prim.attributes.POSITION;
          const nrmAcc = prim.attributes.NORMAL;
          const colAcc = prim.attributes.COLOR_0;
          if (posAcc === undefined || nrmAcc === undefined) continue;
          const positions = accessor(posAcc) as Float32Array;
          const normals = accessor(nrmAcc) as Float32Array;
          const colors =
            colAcc !== undefined
              ? (accessor(colAcc) as Float32Array | Uint8Array)
              : null;
          const colorElements =
            colAcc !== undefined
              ? TYPE_ELEMENTS[json.accessors[colAcc]!.type]!
              : 0;
          const colorIsFloat =
            colAcc !== undefined
              ? json.accessors[colAcc]!.componentType === 5126
              : false;
          const matBase = (() => {
            const matIdx = prim.material;
            if (matIdx === undefined) return [1, 1, 1];
            const mat = json.materials?.[matIdx];
            // Per-name override takes precedence over baseColorFactor.
            if (materialColors && mat?.name && materialColors[mat.name]) {
              const c = materialColors[mat.name]!;
              return [c[0], c[1], c[2]];
            }
            const m = mat?.pbrMetallicRoughness?.baseColorFactor;
            if (!m) return [1, 1, 1];
            return [m[0], m[1], m[2]];
          })();

          const vertexCount = positions.length / 3;
          for (let i = 0; i < vertexCount; i++) {
            const [wx, wy, wz] = transformPoint(
              world,
              positions[i * 3]!,
              positions[i * 3 + 1]!,
              positions[i * 3 + 2]!,
            );
            outPos.push(wx, wy, wz);
            if (wx < bbMinX) bbMinX = wx;
            if (wx > bbMaxX) bbMaxX = wx;
            if (wy < bbMinY) bbMinY = wy;
            if (wy > bbMaxY) bbMaxY = wy;
            if (wz < bbMinZ) bbMinZ = wz;
            if (wz > bbMaxZ) bbMaxZ = wz;
            const [nx, ny, nz] = transformDir(
              world,
              normals[i * 3]!,
              normals[i * 3 + 1]!,
              normals[i * 3 + 2]!,
            );
            outNrm.push(nx, ny, nz);
            if (colors && colorElements >= 3) {
              const div = colorIsFloat ? 1 : 255;
              outCol.push(
                (colors[i * colorElements]! / div) * matBase[0]!,
                (colors[i * colorElements + 1]! / div) * matBase[1]!,
                (colors[i * colorElements + 2]! / div) * matBase[2]!,
              );
            } else {
              outCol.push(matBase[0]!, matBase[1]!, matBase[2]!);
            }
          }

          if (prim.indices !== undefined) {
            const indices = accessor(prim.indices) as
              | Uint16Array
              | Uint32Array
              | Uint8Array;
            for (let i = 0; i < indices.length; i++) {
              outIdx.push(baseVertex + (indices[i] as number));
            }
          } else {
            // Non-indexed: synthesize a strip of incrementing indices.
            for (let i = 0; i < vertexCount; i++) outIdx.push(baseVertex + i);
          }
        }
      }
    }
    if (node.children) for (const c of node.children) visit(c, world);
  };

  const identity = new Float32Array(16);
  identity[0] = 1;
  identity[5] = 1;
  identity[10] = 1;
  identity[15] = 1;
  for (const root of sceneNodes) visit(root, identity);

  if (outPos.length === 0) {
    throw new Error("parseGlb: scene contains no geometry");
  }

  // Interleave into MeshAsset format (9 f32 per vertex).
  const vCount = outPos.length / 3;
  const vertices = new Float32Array(vCount * 9);
  for (let i = 0; i < vCount; i++) {
    const o = i * 9;
    vertices[o] = outPos[i * 3]!;
    vertices[o + 1] = outPos[i * 3 + 1]!;
    vertices[o + 2] = outPos[i * 3 + 2]!;
    vertices[o + 3] = outNrm[i * 3]!;
    vertices[o + 4] = outNrm[i * 3 + 1]!;
    vertices[o + 5] = outNrm[i * 3 + 2]!;
    vertices[o + 6] = outCol[i * 3]!;
    vertices[o + 7] = outCol[i * 3 + 1]!;
    vertices[o + 8] = outCol[i * 3 + 2]!;
  }

  return {
    vertices,
    indices: new Uint32Array(outIdx),
    stride: MESH_VERTEX_STRIDE,
    bbox: { min: [bbMinX, bbMinY, bbMinZ], max: [bbMaxX, bbMaxY, bbMaxZ] },
    vertexCount: vCount,
    indexCount: outIdx.length,
    collisionBoxes: collisionBoxes.length > 0 ? collisionBoxes : undefined,
  };
}

/** Convenience: fetch a .glb URL and parse it. */
export async function loadGlb(
  url: string,
  materialColors?: Record<string, [number, number, number]>,
): Promise<GltfMeshAsset> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`loadGlb(${url}): HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  return parseGlb(buf, materialColors);
}

/**
 * Walk the default scene of a .glb and return the world-space (glTF
 * coordinates, no axis fix or scaling) origin of every NAMED node.
 *
 * Useful for harvesting authoring-time annotations like "Skipper.Head" from
 * Empties dropped into the boat scene by the modeler. The caller is
 * expected to pipe each anchor through the same axis-fix/scale/yaw/yOffset
 * transform that's applied to the mesh — see {@link loadBoatGlb}.
 */
export function extractGlbNamedNodeWorldPositions(
  buf: ArrayBuffer,
): Record<string, [number, number, number]> {
  const dv = new DataView(buf);
  if (dv.getUint32(0, true) !== GLB_MAGIC)
    throw new Error("extractGlbNamedNodeWorldPositions: not a .glb file");
  const jsonLen = dv.getUint32(12, true);
  const jsonBytes = new Uint8Array(buf, 20, jsonLen);
  const json = JSON.parse(new TextDecoder().decode(jsonBytes)) as GltfJson;
  const sceneIdx = json.scene ?? 0;
  const sceneNodes = json.scenes[sceneIdx]?.nodes ?? [];

  // Column-major 4x4 multiply: out = a * b.
  const mul = (a: number[], b: number[]): number[] => {
    const r = new Array<number>(16).fill(0);
    for (let c = 0; c < 4; c++) {
      for (let rIdx = 0; rIdx < 4; rIdx++) {
        let s = 0;
        for (let k = 0; k < 4; k++) s += a[k * 4 + rIdx]! * b[c * 4 + k]!;
        r[c * 4 + rIdx] = s;
      }
    }
    return r;
  };

  const localMatrix = (n: GltfJson["nodes"][number]): number[] => {
    if (n.matrix && n.matrix.length === 16) return n.matrix.slice();
    const t = (n.translation ?? [0, 0, 0]) as [number, number, number];
    const [qx, qy, qz, qw] = (n.rotation ?? [0, 0, 0, 1]) as [
      number,
      number,
      number,
      number,
    ];
    const s = (n.scale ?? [1, 1, 1]) as [number, number, number];
    const xx = qx * qx,
      yy = qy * qy,
      zz = qz * qz;
    const xy = qx * qy,
      xz = qx * qz,
      yz = qy * qz;
    const wx = qw * qx,
      wy = qw * qy,
      wz = qw * qz;
    // Column-major.
    return [
      (1 - 2 * (yy + zz)) * s[0],
      2 * (xy + wz) * s[0],
      2 * (xz - wy) * s[0],
      0,
      2 * (xy - wz) * s[1],
      (1 - 2 * (xx + zz)) * s[1],
      2 * (yz + wx) * s[1],
      0,
      2 * (xz + wy) * s[2],
      2 * (yz - wx) * s[2],
      (1 - 2 * (xx + yy)) * s[2],
      0,
      t[0],
      t[1],
      t[2],
      1,
    ];
  };

  const out: Record<string, [number, number, number]> = {};
  // prettier-ignore
  const I = [1,0,0,0, 0,1,0,0, 0,0,1,0, 0,0,0,1];
  const walk = (idx: number, parent: number[]): void => {
    const n = json.nodes[idx];
    if (!n) return;
    const world = mul(parent, localMatrix(n));
    if (n.name) {
      out[n.name] = [world[12]!, world[13]!, world[14]!];
    }
    for (const child of n.children ?? []) walk(child, world);
  };
  for (const root of sceneNodes) walk(root, I);
  return out;
}

/**
 * Same options as `loadRhino3dm`. Allows a `.glb` boat asset to be plugged
 * into the BoatLibrary without renderer changes.
 */
export interface LoadBoatGlbOptions {
  /** Source axis-up convention. Default "Y" (glTF default). */
  axisUp?: "Y" | "Z";
  /**
   * Source forward (length) axis in the glb file. When set, overrides the
   * largest-extent auto-detection — useful for sailboats whose mast height
   * rivals the hull length and would otherwise be picked as the length
   * axis. Default: undefined (auto-detect).
   */
  axisLength?: "X" | "Y" | "Z";
  /** Yaw (deg→rad) applied after axis fix. Use to align bow with +X. */
  yawRad?: number;
  /** Roll (deg→rad) applied last. */
  rollRad?: number;
  /** If set, model is uniformly scaled so its X-extent matches LOA. */
  targetLOA?: number;
  /**
   * How the source up-axis is aligned to the body Y=0 (waterline) plane:
   *   - "bbox-bottom": shift so the lowest vertex (deepest keel point)
   *     ends up at Y=0. Useful when the modeller authored the boat sitting
   *     on the floor at z=0.
   *   - "raw":         keep the source coordinate — i.e. the modeller has
   *     already authored the waterline at z=0. (Recommended.)
   * Default: "raw".
   */
  yAlign?: "bbox-bottom" | "raw";
  /** Vertical offset (m) so reference point sits at body Y=0 (waterline). */
  yOffset?: number;
  /** Override every vertex colour (toon-style flat colour). */
  overrideColor?: [number, number, number];
  /** Used when no COLOR_0 / material baseColor in the glb. */
  fallbackColor?: [number, number, number];
  /**
   * Reverse triangle winding (and flip recomputed flat normals). Use
   * when faces are invisible from the outside due to Blender authoring
   * with inward-pointing normals.
   */
  flipNormals?: boolean;
  /**
   * Map glTF material names → vertex colour, applied during parse.
   * Useful when the source has no COLOR_0 and no baseColorFactor — only
   * named materials (e.g. Fishing_Ship, Windows, Light).
   */
  materialColors?: Record<string, [number, number, number]>;
  /**
   * Predicate run on every glTF node name. Returning true SKIPS that
   * node's mesh during merge — use this to keep anchor-only Empties
   * (e.g. `Skipper.Head`, `Cleat.Bow`) for position lookup while hiding
   * any debug visualization mesh attached to them.
   */
  hideNode?: (name: string) => boolean;
  verbose?: boolean;
}

/**
 * Load a .glb boat model and bake it into a faceted, body-frame MeshAsset.
 *
 * The pipeline mirrors `loadRhino3dm`:
 *   1. Parse the glb into a single merged mesh (GltfLoader.parseGlb).
 *   2. Auto-detect length/up/beam axes from the AABB.
 *   3. Centre on length+beam midpoint, set bottom of bbox at body Y=0.
 *   4. Scale uniformly so X-extent == targetLOA.
 *   5. Apply optional yaw / roll / Y-offset.
 *   6. Regenerate flat per-triangle normals (toon shader expects faceted).
 *   7. Replace per-vertex colours with `overrideColor` if provided.
 */
export async function loadBoatGlb(
  url: string,
  opts: LoadBoatGlbOptions = {},
): Promise<{
  mesh: import("./MeshAsset").MeshAsset;
  /** Body-frame positions of every NAMED node from the source glb. Keys are
   * the original node names (case preserved). The renderer pipeline never
   * touches these — they're purely for app-level anchor harvesting (e.g.
   * `Skipper.Head` → cockpit camera eye). */
  anchors: Record<string, [number, number, number]>;
}> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`loadBoatGlb(${url}): HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  const src = parseGlb(buf, {
    materialColors: opts.materialColors,
    hideNode: opts.hideNode,
  });
  // Raw glTF-frame world positions of every named node, harvested from the
  // same buffer (no second fetch).
  const namedNodesRaw = extractGlbNamedNodeWorldPositions(buf);

  // ---- 1. Walk triangles ------------------------------------------------
  const srcVerts = src.vertices;
  const srcIdx = src.indices;
  const triCount = srcIdx.length / 3;

  // ---- 2. Axis detection from bbox -------------------------------------
  const min = src.bbox.min;
  const max = src.bbox.max;
  const lens: [number, number, number] = [
    max[0] - min[0],
    max[1] - min[1],
    max[2] - min[2],
  ];
  type Axis = 0 | 1 | 2;
  const sorted: Axis[] = [0, 1, 2] as Axis[];
  sorted.sort((a, b) => lens[b]! - lens[a]!);
  let lengthAxis: Axis;
  if (opts.axisLength === "X") lengthAxis = 0;
  else if (opts.axisLength === "Y") lengthAxis = 1;
  else if (opts.axisLength === "Z") lengthAxis = 2;
  else lengthAxis = sorted[0]!;
  let upAxis: Axis;
  if (opts.axisUp === "Y") upAxis = 1;
  else if (opts.axisUp === "Z") upAxis = 2;
  else upAxis = sorted[2]!;
  if (upAxis === lengthAxis) {
    // Pick the next-largest axis that doesn't collide.
    upAxis = sorted.find((a) => a !== lengthAxis)!;
  }
  const beamAxis: Axis = ([0, 1, 2] as Axis[]).find(
    (a) => a !== lengthAxis && a !== upAxis,
  )!;

  const cLength = (min[lengthAxis]! + max[lengthAxis]!) * 0.5;
  const cBeam = (min[beamAxis]! + max[beamAxis]!) * 0.5;
  // yAlign: "raw" keeps source Y — modeller authored waterline at z=0.
  // "bbox-bottom" shifts so the lowest vertex hits Y=0 (legacy behaviour
  // for models sitting on the floor at z=0). Default "raw".
  const baseUp = (opts.yAlign ?? "raw") === "bbox-bottom" ? min[upAxis]! : 0;
  const srcLen = lens[lengthAxis]!;
  const scale = opts.targetLOA && srcLen > 1e-6 ? opts.targetLOA / srcLen : 1.0;

  const yaw = opts.yawRad ?? 0;
  const roll = opts.rollRad ?? 0;
  const cy2 = Math.cos(yaw),
    sy2 = Math.sin(yaw);
  const cr = Math.cos(roll),
    sr = Math.sin(roll);
  const yOff = opts.yOffset ?? 0;

  const overrideCol = opts.overrideColor;
  const fallback = opts.fallbackColor ?? [0.8, 0.8, 0.8];

  const transformPoint = (
    sp: [number, number, number],
  ): [number, number, number] => {
    let X = sp[lengthAxis] - cLength;
    let Y = sp[upAxis] - baseUp;
    let Z = sp[beamAxis] - cBeam;
    X *= scale;
    Y *= scale;
    Z *= scale;
    // yaw about Y
    const X2 = X * cy2 + Z * sy2;
    const Z2 = -X * sy2 + Z * cy2;
    X = X2;
    Z = Z2;
    // roll about X
    const Y3 = Y * cr - Z * sr;
    const Z3 = Y * sr + Z * cr;
    Y = Y3;
    Z = Z3;
    Y += yOff;
    return [X, Y, Z];
  };

  // ---- 3. Faceted output ----------------------------------------------
  const outV = new Float32Array(triCount * 3 * 9);
  const outI = new Uint32Array(triCount * 3);
  let vi = 0;
  let ii = 0;

  const readSrcCol = (vIdx: number): [number, number, number] => {
    const o = vIdx * 9;
    if (overrideCol) return overrideCol;
    const c0 = srcVerts[o + 6]!;
    const c1 = srcVerts[o + 7]!;
    const c2 = srcVerts[o + 8]!;
    if (c0 === 1 && c1 === 1 && c2 === 1) return fallback;
    return [c0, c1, c2];
  };

  const readSrcPos = (vIdx: number): [number, number, number] => {
    const o = vIdx * 9;
    return [srcVerts[o]!, srcVerts[o + 1]!, srcVerts[o + 2]!];
  };

  for (let t = 0; t < triCount; t++) {
    const i0 = srcIdx[t * 3]!;
    const i1Raw = srcIdx[t * 3 + 1]!;
    const i2Raw = srcIdx[t * 3 + 2]!;
    // Optionally flip winding so flat normals point the other way.
    const i1 = opts.flipNormals ? i2Raw : i1Raw;
    const i2 = opts.flipNormals ? i1Raw : i2Raw;
    const p0 = transformPoint(readSrcPos(i0));
    const p1 = transformPoint(readSrcPos(i1));
    const p2 = transformPoint(readSrcPos(i2));
    // flat normal
    const ax = p1[0] - p0[0],
      ay = p1[1] - p0[1],
      az = p1[2] - p0[2];
    const bx = p2[0] - p0[0],
      by = p2[1] - p0[1],
      bz = p2[2] - p0[2];
    let nx = ay * bz - az * by;
    let ny = az * bx - ax * bz;
    let nz = ax * by - ay * bx;
    const nm = Math.hypot(nx, ny, nz);
    if (nm > 1e-8) {
      nx /= nm;
      ny /= nm;
      nz /= nm;
    } else {
      nx = 0;
      ny = 1;
      nz = 0;
    }
    const c0 = readSrcCol(i0);
    const c1 = readSrcCol(i1);
    const c2 = readSrcCol(i2);
    for (const [p, c] of [
      [p0, c0],
      [p1, c1],
      [p2, c2],
    ] as const) {
      outV[vi++] = p[0];
      outV[vi++] = p[1];
      outV[vi++] = p[2];
      outV[vi++] = nx;
      outV[vi++] = ny;
      outV[vi++] = nz;
      outV[vi++] = c[0];
      outV[vi++] = c[1];
      outV[vi++] = c[2];
    }
    outI[ii++] = ii - 1 - 2;
    outI[ii - 1] = ii - 2;
    // simpler fix below: just set sequential triangle indices
  }

  // Sequential indices (faceted: 3 unique verts per triangle).
  for (let t = 0; t < triCount; t++) {
    outI[t * 3] = t * 3;
    outI[t * 3 + 1] = t * 3 + 1;
    outI[t * 3 + 2] = t * 3 + 2;
  }

  if (opts.verbose) {
    console.log(
      `[loadBoatGlb] ${url}: ${triCount} tri, src LOA ${srcLen.toFixed(2)} m → scale ${scale.toFixed(3)} (lenAxis=${lengthAxis}, upAxis=${upAxis})`,
    );
  }

  // Pipe each named-node anchor through the same transform pipeline so the
  // app sees positions in the same body frame as the rest of the boat
  // (X-fwd, Y-up at waterline, Z-stbd).
  const anchors: Record<string, [number, number, number]> = {};
  for (const [name, p] of Object.entries(namedNodesRaw)) {
    anchors[name] = transformPoint(p);
  }
  if (opts.verbose && Object.keys(anchors).length > 0) {
    const list = Object.entries(anchors)
      .map(
        ([n, a]) =>
          `${n}=(${a[0].toFixed(2)}, ${a[1].toFixed(2)}, ${a[2].toFixed(2)})`,
      )
      .join(", ");
    console.log(`[loadBoatGlb] ${url}: anchors → ${list}`);
  }

  return {
    mesh: {
      vertices: outV,
      indices: outI,
      stride: MESH_VERTEX_STRIDE,
      label: `glb:${url}`,
    },
    anchors,
  };
}

// =====================================================================
// Scene-mode loading: keep root nodes separate, with their pivot transforms.
// Used for boats with animated parts (steering wheels, mast, propeller…).
// =====================================================================

import { MESH_VERTEX_FLOATS } from "./MeshAsset";

export interface BoatScenePart {
  /** glTF node name (e.g. "hull", "wheel.L"). */
  name: string;
  /**
   * Faceted mesh in this part's LOCAL coordinates (no pivot transform applied,
   * but uniform-scaled to match boat target LOA, and axis-fixed to VF frame).
   * "Local" means: origin at this part's pivot, axes aligned with VF body
   * frame after pivot rotation has been applied to the mesh once at load
   * time. (We bake the pivot rotation into the mesh so that animated rotation
   * happens around a clean axis, and the renderer only has to add the
   * translation per part — see BoatRenderPass.)
   */
  mesh: import("./MeshAsset").MeshAsset;
  /**
   * Same vertex stream as `mesh.vertices` but with two extra floats per vertex
   * (`metallic`, `roughness`) appended after `color`. Stride = 11 floats / 44
   * bytes. Indices are identical to `mesh.indices`. Used by the player-boat
   * PBR-ish pipeline; moored boats keep the cheaper 9-float layout.
   */
  verticesPbr: Float32Array;
  /** Pivot translation in VF body-frame coords (m). */
  pivotPosition: [number, number, number];
}

/** Stride (f32 count) of the per-part PBR vertex stream. */
export const SCENE_PART_PBR_FLOATS = 11;
/** Stride (bytes) of the per-part PBR vertex stream. */
export const SCENE_PART_PBR_STRIDE = SCENE_PART_PBR_FLOATS * 4;

export interface BoatScene {
  parts: BoatScenePart[];
  /** AABB of all parts combined, in body-frame coords. */
  bbox: { min: [number, number, number]; max: [number, number, number] };
}

export interface LoadBoatGlbSceneOptions {
  /** Source axis-up. Default "Y" (Blender default glTF export). */
  axisUp?: "Y" | "Z";
  /**
   * Which source axis carries the boat length (bow→stern). Same convention
   * as {@link LoadBoatGlbOptions}. Defaults to the longest non-up axis.
   */
  axisLength?: "X" | "Y" | "Z";
  /** Yaw (rad) applied AFTER axis fix to align bow with +X. */
  yawRad?: number;
  /** Roll (rad) about VF X applied after yaw. */
  rollRad?: number;
  /** If set, model is uniformly scaled so its length-extent matches LOA. */
  targetLOA?: number;
  /** Vertical offset (m) applied last. */
  yOffset?: number;
  /** Map glTF material name → vertex colour. */
  materialColors?: Record<string, [number, number, number]>;
  /** Fallback color for vertices without color/material. */
  fallbackColor?: [number, number, number];
  /** Flip triangle winding so flat normals point the other way. */
  flipNormals?: boolean;
  verbose?: boolean;
}

/**
 * Parse a .glb file into a list of parts, one per root scene node.
 *
 * Each part keeps its own pivot position so that runtime animation (e.g.
 * rotating a steering wheel) can simply add a local rotation around that
 * pivot without affecting the rest of the boat.
 *
 * Mesh vertices for each part are baked into VF body-frame coords:
 *   - axis fix (glTF Y-up → VF +X forward, +Y up, +Z stbd)
 *   - uniform scale to targetLOA
 *   - per-node rotation/scale baked in (so `R Y θ` in app == rotate around
 *     this part's local Y axis after rotation has been removed at load time)
 *   - translation NOT baked in — kept as `pivotPosition` for animation.
 */
export async function loadBoatGlbScene(
  url: string,
  opts: LoadBoatGlbSceneOptions = {},
): Promise<BoatScene> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`loadBoatGlbScene(${url}): HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  const dv = new DataView(buf);
  if (dv.getUint32(0, true) !== GLB_MAGIC) throw new Error("not a .glb file");
  const jsonLen = dv.getUint32(12, true);
  const jsonBytes = new Uint8Array(buf, 20, jsonLen);
  const json = JSON.parse(new TextDecoder().decode(jsonBytes)) as GltfJson;
  const chunk1Off = 20 + jsonLen;
  const binLen = dv.getUint32(chunk1Off, true);
  const binType = dv.getUint32(chunk1Off + 4, true);
  if (binType !== CHUNK_BIN) throw new Error("missing BIN chunk");
  const bin = buf.slice(chunk1Off + 8, chunk1Off + 8 + binLen);

  const accessor = (idx: number): ArrayBufferView => {
    const acc = json.accessors[idx]!;
    const view = json.bufferViews[acc.bufferView]!;
    const baseOffset = (view.byteOffset ?? 0) + (acc.byteOffset ?? 0);
    const elements = TYPE_ELEMENTS[acc.type]!;
    const Ctor = COMPONENT_CTOR[acc.componentType]!;
    return new Ctor(bin, baseOffset, acc.count * elements);
  };

  // Quaternion → 3x3 matrix (column-major).
  const quatToMat3 = (q: [number, number, number, number]): number[] => {
    const [x, y, z, w] = q;
    const xx = x * x,
      yy = y * y,
      zz = z * z;
    const xy = x * y,
      xz = x * z,
      yz = y * z;
    const wx = w * x,
      wy = w * y,
      wz = w * z;
    return [
      1 - 2 * (yy + zz),
      2 * (xy + wz),
      2 * (xz - wy),
      2 * (xy - wz),
      1 - 2 * (xx + zz),
      2 * (yz + wx),
      2 * (xz + wy),
      2 * (yz - wx),
      1 - 2 * (xx + yy),
    ];
  };

  // Axis fix is handled later in Pass 2 via the lengthAxis/upAxis/beamAxis
  // remap, mirroring `loadBoatGlb`. Raw bbox in glTF space is collected here.

  // ---- Pass 1: collect root nodes + raw bbox to derive scale ------------
  const sceneIdx = json.scene ?? 0;
  const sceneNodes = json.scenes[sceneIdx]?.nodes ?? [];

  let bbMinX = Infinity,
    bbMinY = Infinity,
    bbMinZ = Infinity;
  let bbMaxX = -Infinity,
    bbMaxY = -Infinity,
    bbMaxZ = -Infinity;

  const mat3Mul = (a: number[], b: number[]): number[] => [
    a[0]! * b[0]! + a[3]! * b[1]! + a[6]! * b[2]!,
    a[1]! * b[0]! + a[4]! * b[1]! + a[7]! * b[2]!,
    a[2]! * b[0]! + a[5]! * b[1]! + a[8]! * b[2]!,
    a[0]! * b[3]! + a[3]! * b[4]! + a[6]! * b[5]!,
    a[1]! * b[3]! + a[4]! * b[4]! + a[7]! * b[5]!,
    a[2]! * b[3]! + a[5]! * b[4]! + a[8]! * b[5]!,
    a[0]! * b[6]! + a[3]! * b[7]! + a[6]! * b[8]!,
    a[1]! * b[6]! + a[4]! * b[7]! + a[7]! * b[8]!,
    a[2]! * b[6]! + a[5]! * b[7]! + a[8]! * b[8]!,
  ];

  const mat3Vec = (
    m: number[],
    v: [number, number, number],
  ): [number, number, number] => [
    m[0]! * v[0] + m[3]! * v[1] + m[6]! * v[2],
    m[1]! * v[0] + m[4]! * v[1] + m[7]! * v[2],
    m[2]! * v[0] + m[5]! * v[1] + m[8]! * v[2],
  ];

  type NodeXform = {
    translation: [number, number, number];
    rotation: number[];
    scale: [number, number, number];
  };

  type RawPart = {
    name: string;
    pivotGlTF: [number, number, number];
    rotMat: number[]; // 3x3 column-major
    nodeScale: [number, number, number];
    triangles: {
      positions: [number, number, number][];
      colors: [number, number, number][];
      /** Per-vertex (metallic, roughness) sampled from primitive material. */
      metRough: [number, number][];
    };
  };
  const rawParts: RawPart[] = [];

  const collectNode = (nodeIdx: number, parent: NodeXform): void => {
    const node = json.nodes[nodeIdx];
    if (!node) return;
    const localT = (node.translation ?? [0, 0, 0]) as [number, number, number];
    const localR = (node.rotation ?? [0, 0, 0, 1]) as [
      number,
      number,
      number,
      number,
    ];
    const localS = (node.scale ?? [1, 1, 1]) as [number, number, number];
    const scaledLocalT: [number, number, number] = [
      localT[0]! * parent.scale[0]!,
      localT[1]! * parent.scale[1]!,
      localT[2]! * parent.scale[2]!,
    ];
    const rotatedLocalT = mat3Vec(parent.rotation, scaledLocalT);
    const t: [number, number, number] = [
      parent.translation[0]! + rotatedLocalT[0]!,
      parent.translation[1]! + rotatedLocalT[1]!,
      parent.translation[2]! + rotatedLocalT[2]!,
    ];
    const rotMat = mat3Mul(parent.rotation, quatToMat3(localR));
    const s: [number, number, number] = [
      parent.scale[0]! * localS[0]!,
      parent.scale[1]! * localS[1]!,
      parent.scale[2]! * localS[2]!,
    ];

    if (node.mesh === undefined) {
      // Empty / pivot-only node — still emit as a 0-triangle part so that
      // callers can read its pivot position in the unified VF body frame
      // (handy for attaching physics-driven children, e.g. fender ropes).
      rawParts.push({
        name: node.name ?? `node_${nodeIdx}`,
        pivotGlTF: [t[0]!, t[1]!, t[2]!],
        rotMat,
        nodeScale: [s[0]!, s[1]!, s[2]!],
        triangles: {
          positions: [],
          colors: [],
          metRough: [],
        },
      });
      for (const childIdx of node.children ?? []) {
        collectNode(childIdx, { translation: t, rotation: rotMat, scale: s });
      }
      return;
    }
    const mesh = json.meshes[node.mesh];
    if (!mesh) return;

    const trianglePositions: [number, number, number][] = [];
    const triangleColors: [number, number, number][] = [];
    const triangleMetRough: [number, number][] = [];

    for (const prim of mesh.primitives) {
      const posAcc = prim.attributes.POSITION;
      if (posAcc === undefined) continue;
      const positions = accessor(posAcc) as Float32Array;
      const colAcc = prim.attributes.COLOR_0;
      const colors =
        colAcc !== undefined
          ? (accessor(colAcc) as Float32Array | Uint8Array)
          : null;
      const colorElements =
        colAcc !== undefined ? TYPE_ELEMENTS[json.accessors[colAcc]!.type]! : 0;
      const colorIsFloat =
        colAcc !== undefined
          ? json.accessors[colAcc]!.componentType === 5126
          : false;
      const matBase = ((): [number, number, number] => {
        const matIdx = prim.material;
        if (matIdx === undefined) return [1, 1, 1];
        const mat = json.materials?.[matIdx];
        if (opts.materialColors && mat?.name && opts.materialColors[mat.name]) {
          const c = opts.materialColors[mat.name]!;
          return [c[0], c[1], c[2]];
        }
        const m = mat?.pbrMetallicRoughness?.baseColorFactor;
        if (!m) return [1, 1, 1];
        return [m[0]!, m[1]!, m[2]!];
      })();
      // glTF spec defaults: metallicFactor=1.0, roughnessFactor=1.0 when
      // pbrMetallicRoughness is present but factors omitted. If material has
      // no pbrMetallicRoughness at all, treat as non-metal matte (0, 1).
      const matMetRough = ((): [number, number] => {
        const matIdx = prim.material;
        if (matIdx === undefined) return [0, 1];
        const pbr = json.materials?.[matIdx]?.pbrMetallicRoughness;
        if (!pbr) return [0, 1];
        const m = pbr.metallicFactor;
        const r = pbr.roughnessFactor;
        return [m ?? 1, r ?? 1];
      })();

      let indices: ArrayLike<number>;
      if (prim.indices !== undefined) {
        indices = accessor(prim.indices) as
          | Uint16Array
          | Uint32Array
          | Uint8Array;
      } else {
        const arr = new Uint32Array(positions.length / 3);
        for (let i = 0; i < arr.length; i++) arr[i] = i;
        indices = arr;
      }
      for (let ti = 0; ti < indices.length; ti += 3) {
        const ia = indices[ti]!,
          ib = indices[ti + 1]!,
          ic = indices[ti + 2]!;
        for (const vi of [ia, ib, ic]) {
          const px = positions[vi * 3]! * s[0]!;
          const py = positions[vi * 3 + 1]! * s[1]!;
          const pz = positions[vi * 3 + 2]! * s[2]!;
          // Apply node rotation. Translation NOT applied here — it stays as pivot.
          const rx = rotMat[0]! * px + rotMat[3]! * py + rotMat[6]! * pz;
          const ry = rotMat[1]! * px + rotMat[4]! * py + rotMat[7]! * pz;
          const rz = rotMat[2]! * px + rotMat[5]! * py + rotMat[8]! * pz;
          // World-space bbox uses translation too.
          const wx = rx + t[0]!,
            wy = ry + t[1]!,
            wz = rz + t[2]!;
          if (wx < bbMinX) bbMinX = wx;
          if (wy < bbMinY) bbMinY = wy;
          if (wz < bbMinZ) bbMinZ = wz;
          if (wx > bbMaxX) bbMaxX = wx;
          if (wy > bbMaxY) bbMaxY = wy;
          if (wz > bbMaxZ) bbMaxZ = wz;
          trianglePositions.push([rx, ry, rz]);
          let cr = matBase[0]!,
            cg = matBase[1]!,
            cb = matBase[2]!;
          if (colors && colorElements >= 3) {
            const div = colorIsFloat ? 1 : 255;
            cr *= colors[vi * colorElements]! / div;
            cg *= colors[vi * colorElements + 1]! / div;
            cb *= colors[vi * colorElements + 2]! / div;
          }
          triangleColors.push([cr, cg, cb]);
          triangleMetRough.push([matMetRough[0]!, matMetRough[1]!]);
        }
      }
    }

    rawParts.push({
      name: node.name ?? `node_${nodeIdx}`,
      pivotGlTF: [t[0]!, t[1]!, t[2]!],
      rotMat,
      nodeScale: [s[0]!, s[1]!, s[2]!],
      triangles: {
        positions: trianglePositions,
        colors: triangleColors,
        metRough: triangleMetRough,
      },
    });
    for (const childIdx of node.children ?? []) {
      collectNode(childIdx, { translation: t, rotation: rotMat, scale: s });
    }
  };

  for (const rootIdx of sceneNodes) {
    collectNode(rootIdx, {
      translation: [0, 0, 0],
      rotation: [1, 0, 0, 0, 1, 0, 0, 0, 1],
      scale: [1, 1, 1],
    });
  }

  // ---- Pass 2: derive length/up/beam axis mapping to VF body frame -------
  // Same convention as `loadBoatGlb`: pick lengthAxis from `axisLength`
  // (or fall back to the longest extent), upAxis from `axisUp`, and the
  // remaining axis becomes beamAxis. Then VF.x = src[lengthAxis] − cLength,
  // VF.y = src[upAxis], VF.z = src[beamAxis] − cBeam — yaw + yOffset applied
  // afterwards. This must mirror the flat loader so a model with
  // `axisLength: "Z"` (Blender +Y forward → glTF −Z forward) renders
  // identically in both modes.
  const rawMin: [number, number, number] = [bbMinX, bbMinY, bbMinZ];
  const rawMax: [number, number, number] = [bbMaxX, bbMaxY, bbMaxZ];
  const rawLens: [number, number, number] = [
    rawMax[0] - rawMin[0],
    rawMax[1] - rawMin[1],
    rawMax[2] - rawMin[2],
  ];
  type Axis = 0 | 1 | 2;
  const sortedAxes: Axis[] = [0, 1, 2] as Axis[];
  sortedAxes.sort((a, b) => rawLens[b]! - rawLens[a]!);
  let lengthAxis: Axis;
  if (opts.axisLength === "X") lengthAxis = 0;
  else if (opts.axisLength === "Y") lengthAxis = 1;
  else if (opts.axisLength === "Z") lengthAxis = 2;
  else lengthAxis = sortedAxes[0]!;
  let upAxis: Axis;
  if (opts.axisUp === "Y") upAxis = 1;
  else if (opts.axisUp === "Z") upAxis = 2;
  else upAxis = sortedAxes[2]!;
  if (upAxis === lengthAxis) {
    upAxis = sortedAxes.find((a) => a !== lengthAxis)!;
  }
  const beamAxis: Axis = ([0, 1, 2] as Axis[]).find(
    (a) => a !== lengthAxis && a !== upAxis,
  )!;

  const cLength = (rawMin[lengthAxis]! + rawMax[lengthAxis]!) * 0.5;
  const cBeam = (rawMin[beamAxis]! + rawMax[beamAxis]!) * 0.5;
  const srcLen = rawLens[lengthAxis]!;
  const scale = opts.targetLOA && srcLen > 1e-6 ? opts.targetLOA / srcLen : 1.0;

  const yaw = opts.yawRad ?? 0;
  const roll = opts.rollRad ?? 0;
  const cy = Math.cos(yaw),
    sy = Math.sin(yaw);
  const cR = Math.cos(roll),
    sR = Math.sin(roll);
  const yOff = opts.yOffset ?? 0;
  const fallback = opts.fallbackColor ?? [0.8, 0.8, 0.8];

  /**
   * Map a glTF-space point to VF body frame. `centered` controls whether
   * length/beam centering is subtracted — true for pivots (the boat as a
   * whole sits at origin), false for in-part vertex offsets relative to
   * the pivot. yOffset is added only to the centered (pivot) path so the
   * whole boat lifts uniformly.
   */
  const remap = (
    sp: [number, number, number],
    centered: boolean,
  ): [number, number, number] => {
    let X = sp[lengthAxis]!;
    let Y = sp[upAxis]!;
    let Z = sp[beamAxis]!;
    if (centered) {
      X -= cLength;
      Z -= cBeam;
    }
    X *= scale;
    Y *= scale;
    Z *= scale;
    // yaw about Y
    const X2 = X * cy + Z * sy;
    const Z2 = -X * sy + Z * cy;
    X = X2;
    Z = Z2;
    // roll about X
    const Y3 = Y * cR - Z * sR;
    const Z3 = Y * sR + Z * cR;
    Y = Y3;
    Z = Z3;
    if (centered) Y += yOff;
    return [X, Y, Z];
  };

  const parts: BoatScenePart[] = [];
  let outMinX = Infinity,
    outMinY = Infinity,
    outMinZ = Infinity;
  let outMaxX = -Infinity,
    outMaxY = -Infinity,
    outMaxZ = -Infinity;

  for (const rp of rawParts) {
    // Pivot lives in glTF world space (rotMat-baked translation), centered
    // and remapped into VF body frame.
    const piv = remap(rp.pivotGlTF, true);
    const pivotX = piv[0]!;
    const pivotY = piv[1]!;
    const pivotZ = piv[2]!;

    const triCount = rp.triangles.positions.length / 3;
    const outV = new Float32Array(triCount * 3 * MESH_VERTEX_FLOATS);
    const outVpbr = new Float32Array(triCount * 3 * SCENE_PART_PBR_FLOATS);
    const outI = new Uint32Array(triCount * 3);
    let vi = 0;
    let vp = 0;

    for (let t = 0; t < triCount; t++) {
      const transformedPts: [number, number, number][] = [];
      // Optionally flip winding so flat normals point the other way.
      const order: [number, number, number] = opts.flipNormals
        ? [0, 2, 1]
        : [0, 1, 2];
      for (const k of order) {
        const p = rp.triangles.positions[t * 3 + k]!;
        transformedPts.push(remap(p, false));
      }
      const [p0, p1, p2] = transformedPts as [
        [number, number, number],
        [number, number, number],
        [number, number, number],
      ];
      // Flat normal.
      const ax = p1[0] - p0[0],
        ay = p1[1] - p0[1],
        az = p1[2] - p0[2];
      const bx = p2[0] - p0[0],
        by = p2[1] - p0[1],
        bz = p2[2] - p0[2];
      let nx = ay * bz - az * by;
      let ny = az * bx - ax * bz;
      let nz = ax * by - ay * bx;
      const nm = Math.hypot(nx, ny, nz);
      if (nm > 1e-8) {
        nx /= nm;
        ny /= nm;
        nz /= nm;
      } else {
        nx = 0;
        ny = 1;
        nz = 0;
      }
      for (let k = 0; k < 3; k++) {
        const p = transformedPts[k]!;
        const c = rp.triangles.colors[t * 3 + k]!;
        // metRough is per-material (same value for all 3 vertices of a tri),
        // so any in-triangle index works regardless of flipNormals reorder.
        const mr = rp.triangles.metRough[t * 3 + k]!;
        const useColor = c[0] === 1 && c[1] === 1 && c[2] === 1 ? fallback : c;
        outV[vi++] = p[0];
        outV[vi++] = p[1];
        outV[vi++] = p[2];
        outV[vi++] = nx;
        outV[vi++] = ny;
        outV[vi++] = nz;
        outV[vi++] = useColor[0]!;
        outV[vi++] = useColor[1]!;
        outV[vi++] = useColor[2]!;
        outVpbr[vp++] = p[0];
        outVpbr[vp++] = p[1];
        outVpbr[vp++] = p[2];
        outVpbr[vp++] = nx;
        outVpbr[vp++] = ny;
        outVpbr[vp++] = nz;
        outVpbr[vp++] = useColor[0]!;
        outVpbr[vp++] = useColor[1]!;
        outVpbr[vp++] = useColor[2]!;
        outVpbr[vp++] = mr[0]!;
        outVpbr[vp++] = mr[1]!;
        // Bbox in world body-frame: vertex+pivot.
        const wx = p[0] + pivotX,
          wy = p[1] + pivotY,
          wz = p[2] + pivotZ;
        if (wx < outMinX) outMinX = wx;
        if (wy < outMinY) outMinY = wy;
        if (wz < outMinZ) outMinZ = wz;
        if (wx > outMaxX) outMaxX = wx;
        if (wy > outMaxY) outMaxY = wy;
        if (wz > outMaxZ) outMaxZ = wz;
      }
    }
    for (let i = 0; i < triCount * 3; i++) outI[i] = i;

    parts.push({
      name: rp.name,
      mesh: {
        vertices: outV,
        indices: outI,
        stride: MESH_VERTEX_STRIDE,
        label: `glb-scene:${rp.name}`,
      },
      verticesPbr: outVpbr,
      pivotPosition: [pivotX, pivotY, pivotZ],
    });
  }

  if (opts.verbose) {
    console.log(
      `[loadBoatGlbScene] ${url}: ${parts.length} parts, scale=${scale.toFixed(3)}, bbox X=[${outMinX.toFixed(2)}..${outMaxX.toFixed(2)}] Y=[${outMinY.toFixed(2)}..${outMaxY.toFixed(2)}] Z=[${outMinZ.toFixed(2)}..${outMaxZ.toFixed(2)}]`,
    );
    for (const p of parts) {
      console.log(
        `  - ${p.name}: pivot=(${p.pivotPosition.map((v) => v.toFixed(2)).join(", ")})`,
      );
    }
  }

  return {
    parts,
    bbox: {
      min: [outMinX, outMinY, outMinZ],
      max: [outMaxX, outMaxY, outMaxZ],
    },
  };
}
